import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  ArrowRight,
  BarChart2,
  BadgeCheck,
  BookOpen,
  Building2,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  ClipboardCheck,
  CreditCard,
  DollarSign,
  Lock,
  MapPin,
  MessageSquare,
  Pencil,
  Sparkles,
  Tag,
  TrendingUp,
  Users,
  Wallet,
  X,
  type LucideIcon,
} from "lucide-react";
import { ShowMoreList } from "@/components/BuyBoxPanel";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MultiSearchableSelect } from "@/components/ui/multi-searchable-select";
import { useToast } from "@/components/ui/toast";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { sdeExceedsBudget } from "@/lib/buyerProfile";
import type { User as UserType } from "@/types";

interface Category { id: number; name: string }
/** Lookup row; states carry country_id and cities carry state_id so the
    location cascade can prune children client-side without a round trip. */
interface LookupItem { id: number; name: string; country_id?: number | null; state_id?: number | null }

/* MultiSearchableSelect ships the compact filter-drawer sizing; the wizard's
   controls are taller and rounder, so the location selects override it. */
const LOC_SELECT_CLS =
  "h-auto min-h-[3.25rem] rounded-[1.1rem] border-border/70 bg-white/80 px-4 py-3 text-sm";

/* Five steps, grouped by what the answer describes: who the buyer is, how big a
   deal they want, what they want, where, and a final read-back. This replaced
   nine one-question screens that asked exactly the same things — spread thin
   enough that the wizard read as a chore before a buyer reached the end. */
const STEPS: { label: string; icon: LucideIcon; title: string; sub: string }[] = [
  {
    label: "Profile",
    icon: BadgeCheck,
    title: "Tell us about you as a buyer",
    sub: "Your experience and how you plan to pay — what sellers read first.",
  },
  {
    label: "Deal size",
    icon: DollarSign,
    title: "What size deal are you after?",
    sub: "Budget, earnings and how established the business should be.",
  },
  {
    label: "Industries",
    icon: Tag,
    title: "What's your Buy Box?",
    sub: "Select every business category you'd consider.",
  },
  {
    label: "Locations",
    icon: MapPin,
    title: "Where are you buying?",
    sub: "Pick every country, state and city you'd consider — as many as you like.",
  },
  {
    label: "Review",
    icon: ClipboardCheck,
    title: "Review your Buy Box",
    sub: "Check it over, add anything else sellers should know, and submit.",
  },
];
const TOTAL_STEPS = STEPS.length;

/* The questions inside each step, in order. Nothing else in the wizard hard-codes
   which step owns which question: step validity, the on-step question index, the
   "still needed" hint and the Review pencils all read this map. */
const STEP_SECTIONS: Record<number, string[]> = {
  1: ["experience", "funding", "prequal"],
  2: ["budget", "sde", "years"],
  3: ["industries"],
  4: ["locations"],
  5: ["review", "notes"],
};

/** Question id → the step that owns it, for the Review step's edit links. */
const SECTION_STEP: Record<string, number> = Object.fromEntries(
  Object.entries(STEP_SECTIONS).flatMap(([n, ids]) => ids.map((id) => [id, Number(n)])),
);

const SECTION_META: Record<string, {
  icon: LucideIcon;
  /** Heading above the question itself. */
  title: string;
  /** Two words at most — this is the chip in the on-step index. */
  short: string;
  hint?: string;
  optional?: boolean;
}> = {
  experience: {
    icon: BookOpen,
    title: "Your buying experience",
    short: "Experience",
    hint: "This helps sellers understand your background.",
  },
  funding: {
    icon: CreditCard,
    title: "How will you fund this?",
    short: "Funding",
    hint: "Pick every method you'd consider using.",
  },
  prequal: {
    icon: BadgeCheck,
    title: "Are you pre-qualified?",
    short: "Pre-qualified",
    hint: "Hold both? Select both. Not pre-qualified yet? Leave these unselected — you can add it later from your profile.",
    optional: true,
  },
  budget: {
    icon: DollarSign,
    title: "Your budget range",
    short: "Budget",
    hint: "Set a range that matches your acquisition goals.",
  },
  sde: {
    icon: BarChart2,
    title: "SDE preference",
    short: "SDE",
    hint: "What level of Seller Discretionary Earnings are you targeting?",
  },
  years: {
    icon: Calendar,
    title: "Years in business",
    short: "Years",
    hint: "How long should the business have been operating?",
  },
  industries: {
    icon: Tag,
    title: "Business categories",
    short: "Industries",
  },
  locations: {
    icon: MapPin,
    title: "Preferred locations",
    short: "Locations",
  },
  review: {
    icon: ClipboardCheck,
    title: "Your answers",
    short: "Review",
    hint: "Tap the pencil on any row to jump back and change it.",
  },
  notes: {
    icon: MessageSquare,
    title: "Anything else to share?",
    short: "Notes",
    hint: "Optional, but helpful for sellers.",
    optional: true,
  },
};

const OTHER_CATEGORY_ID = -1;
const MAX_BUDGET = 10_000_000;
const SLIDER_STEP = 50_000;

const EXPERIENCE_OPTIONS = [
  {
    value: "first_time",
    label: "First-time buyer",
    sub: "This is my first business acquisition",
    icon: BookOpen,
  },
  {
    value: "bought_1_2",
    label: "I've bought 1–2 businesses",
    sub: "I have some acquisition experience",
    icon: TrendingUp,
  },
  {
    value: "bought_3_plus",
    label: "I've bought 3+ businesses",
    sub: "I'm a seasoned business buyer",
    icon: Sparkles,
  },
] as const;

const BUDGET_PRESETS = [
  { label: "< $250K",       min: 0,         max: 250_000   },
  { label: "$250K – $500K", min: 250_000,   max: 500_000   },
  { label: "$500K – $1M",   min: 500_000,   max: 1_000_000 },
  { label: "$1M – $3M",     min: 1_000_000, max: 3_000_000 },
  { label: "$3M – $5M",     min: 3_000_000, max: 5_000_000 },
  { label: "> $5M",         min: 5_000_000, max: null       },
] as const;

const FUNDING_OPTIONS = [
  { value: "sba_loan",          label: "SBA Loan",          sub: "Government-backed financing",  icon: Building2   },
  { value: "seller_financing",  label: "Seller Financing",  sub: "Payment plan from the seller", icon: TrendingUp  },
  { value: "self_funded",       label: "Self-Funded",       sub: "Using personal capital",       icon: Wallet      },
  { value: "private_lenders",   label: "Private Lenders",  sub: "Investors or private credit",  icon: Users       },
] as const;

/* Lending pre-qualification. Deliberately separate from FUNDING_OPTIONS: how a
   buyer INTENDS to pay is a preference, whereas this is a claim about where they
   already stand with a lender — sellers read the two very differently. */
const PREQUAL_OPTIONS = [
  {
    value: "sba",
    label: "SBA Pre-Qualified",
    sub: "A lender has pre-qualified me for SBA financing",
    icon: Building2,
  },
  {
    value: "private_lender",
    label: "Private Lender Pre-Qualified",
    sub: "A private lender or credit fund has pre-qualified me",
    icon: Wallet,
  },
] as const;

const YEARS_OPTIONS = [
  { value: "any",     label: "Any",          sub: "No preference"      },
  { value: "under_1", label: "< 1 Year",     sub: "Brand new venture"  },
  { value: "1_3",     label: "1 – 3 Years",  sub: "Early stage"        },
  { value: "3_5",     label: "3 – 5 Years",  sub: "Growth phase"       },
  { value: "5_10",    label: "5 – 10 Years", sub: "Established"        },
  { value: "10_plus", label: "10+ Years",    sub: "Highly established" },
] as const;

const SDE_OPTIONS = [
  { value: "100k",    label: "$100K" },
  { value: "250k",    label: "$250K" },
  { value: "500k",    label: "$500K" },
  { value: "1m_plus", label: "$1M+"  },
] as const;

function fmtBudget(n: number): string {
  if (n === 0) return "$0";
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `$${m % 1 === 0 ? m : m.toFixed(1)}M`;
  }
  return `$${Math.round(n / 1000)}K`;
}

const labelOf = (opts: readonly { value: string; label: string }[], v: string) =>
  opts.find((o) => o.value === v)?.label ?? "";

const labelsOf = (opts: readonly { value: string; label: string }[], vs: string[]) =>
  vs.map((v) => labelOf(opts, v)).filter(Boolean);

/* ── Labelled stepper. Doubles as navigation: any step already cleared can be
      clicked, which is what makes the Review step's edit links cheap. ── */
function Stepper({
  step,
  canJump,
  onJump,
}: {
  step: number;
  canJump: (target: number) => boolean;
  onJump: (target: number) => void;
}) {
  return (
    <div className="flex gap-1.5">
      {STEPS.map((s, i) => {
        const n = i + 1;
        const reached = n <= step;
        const jumpable = canJump(n);
        return (
          <button
            key={s.label}
            type="button"
            disabled={!jumpable}
            onClick={() => onJump(n)}
            aria-current={n === step ? "step" : undefined}
            aria-label={`Step ${n}: ${s.label}`}
            className={cn(
              "group flex flex-1 flex-col gap-1 rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
              jumpable ? "cursor-pointer" : "cursor-default",
            )}
          >
            <span
              className={cn(
                "h-1 w-full rounded-full transition-all duration-500",
                reached ? "bg-primary" : "bg-border/60",
                jumpable && n !== step && "group-hover:bg-primary/70",
              )}
            />
            <span
              className={cn(
                "hidden truncate text-[0.65rem] font-bold uppercase tracking-[0.12em] transition-colors sm:block",
                n === step
                  ? "text-primary"
                  : reached
                    ? "text-muted-foreground"
                    : "text-muted-foreground/40",
              )}
            >
              {s.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ── One question inside a step. Steps hold up to three of these, so each says
      "2 of 3" in its own heading: with the rest of the step below the fold, the
      count is what tells a buyer to keep scrolling. ── */
function Section({
  id,
  icon: Icon,
  title,
  hint,
  optional,
  index,
  count,
  children,
}: {
  id: string;
  icon: LucideIcon;
  title: string;
  hint?: string;
  optional?: boolean;
  index: number;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section
      data-section={id}
      className={cn("scroll-mt-4 space-y-2.5", index > 1 && "border-t border-border/60 pt-5")}
    >
      <div className="flex items-start gap-2.5">
        <span className="mt-px flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/10">
          <Icon className="h-4 w-4" strokeWidth={1.7} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {count > 1 && (
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-primary/70">
                {index} of {count}
              </span>
            )}
            <h3 className="heading-bold text-base leading-tight text-foreground">{title}</h3>
            {optional && (
              <span className="rounded-full border border-border/70 px-1.5 py-px text-[0.68rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                Optional
              </span>
            )}
          </div>
          {hint && <p className="mt-0.5 text-xs leading-snug text-muted-foreground">{hint}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

/* ── The step's questions at a glance, right under the step heading. Answers
      the "is this everything?" question a scrolling step otherwise leaves open,
      and each chip scrolls to its question. ── */
function QuestionIndex({
  sections,
  onJump,
}: {
  sections: { id: string; short: string; answered: boolean; optional?: boolean }[];
  onJump: (id: string) => void;
}) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <p className="text-[0.65rem] font-bold uppercase tracking-[0.14em] text-muted-foreground/70">
        {sections.length} questions on this step
      </p>
      <div className="flex flex-wrap items-center justify-center gap-1.5">
        {sections.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => onJump(s.id)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
              s.answered
                ? "border-primary/50 bg-primary/10 text-primary"
                : "border-border/70 bg-white/70 text-muted-foreground hover:border-primary/40 hover:text-foreground",
            )}
          >
            <span
              className={cn(
                "flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[0.65rem] font-bold",
                s.answered ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
              )}
            >
              {s.answered ? <Check className="h-2.5 w-2.5" strokeWidth={3.5} /> : i + 1}
            </span>
            {s.short}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Multi-select check card ── */
function CheckCard({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group flex w-full items-center gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
        selected
          ? "border-primary/70 bg-primary/5 shadow-sm"
          : "border-border/70 bg-white/80 hover:border-primary/30 hover:bg-primary/[0.03]",
      )}
    >
      <span
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-all duration-150",
          selected
            ? "border-primary bg-primary"
            : "border-muted-foreground/30 group-hover:border-primary/50",
        )}
      >
        {selected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
      </span>
      {children}
    </button>
  );
}

/* ── Radio selection card ── */
function OptionCard({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group flex w-full items-center gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
        selected
          ? "border-primary/70 bg-primary/5 shadow-sm"
          : "border-border/70 bg-white/80 hover:border-primary/30 hover:bg-primary/[0.03]",
      )}
    >
      <span
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-150",
          selected
            ? "border-primary bg-primary"
            : "border-muted-foreground/30 group-hover:border-primary/50",
        )}
      >
        {selected && <CheckCircle2 className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />}
      </span>
      {children}
    </button>
  );
}

/* ════════════════════════════════════════════ */

interface Props {
  forceOpen?: boolean;
  onClose?: () => void;
}

export default function BuyerPreferencesModal({ forceOpen = false, onClose }: Props) {
  const { user, updateUser } = useAuth();
  const toast = useToast();

  const [step, setStep] = useState(1);
  const [experience, setExperience] = useState("");
  const [budgetMin, setBudgetMin] = useState(0);
  const [budgetMax, setBudgetMax] = useState<number | null>(null);
  const [budgetTouched, setBudgetTouched] = useState(false);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [otherCategory, setOtherCategory] = useState("");
  const [yearsInBusiness, setYearsInBusiness] = useState("");
  const [fundingMethods, setFundingMethods] = useState<string[]>([]);
  const [prequalifications, setPrequalifications] = useState<string[]>([]);
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  // Structured location: buyers target ANY NUMBER of countries, states and
  // cities, all chosen from the same lookups the listing forms use (no more
  // free-text "esfew" locations). Ids are held as strings — that's what
  // MultiSearchableSelect speaks — and converted on submit.
  const [prefCountryIds, setPrefCountryIds] = useState<string[]>([]);
  const [prefStateIds, setPrefStateIds] = useState<string[]>([]);
  const [prefCityIds, setPrefCityIds] = useState<string[]>([]);
  // Buyer-typed cities missing from the cities lookup ("Add …").
  const [prefCityNames, setPrefCityNames] = useState<string[]>([]);
  const [countries, setCountries] = useState<LookupItem[]>([]);
  const [states, setStates] = useState<LookupItem[]>([]);
  const [cities, setCities] = useState<LookupItem[]>([]);
  // Set during hydration when the buyer has no saved location at all, so the
  // country select can default to the US once the lookup lands.
  const defaultCountryRef = useRef(false);
  const [sdePreference, setSdePreference] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // The scroll container, so a step change starts the new step at the top
  // instead of wherever the previous (now longer) step was left.
  const bodyRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // Set by a Review pencil so the step it switches to opens at that question.
  const pendingSectionRef = useRef<string | null>(null);
  // Whether the step continues below the fold. A step holding two or three
  // questions almost always does, and nothing on screen said so.
  const [moreBelow, setMoreBelow] = useState(false);
  // Declared above the early return: the effects below call it even on the
  // render where the wizard is closed.
  const measureOverflow = () => {
    const el = bodyRef.current;
    if (el) setMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 24);
  };

  const isOpen = forceOpen || Boolean(user?.needs_buyer_preferences);

  useEffect(() => {
    if (!isOpen) return;
    const p = user?.buyer_preferences;
    setStep(1);
    setExperience(p?.experience ?? "");
    setBudgetMin(p?.budget_min ?? 0);
    setBudgetMax(p?.budget_max ?? null);
    setBudgetTouched(p != null && (p.budget_min != null || p.budget_max != null));
    setCategoryIds(p?.category_ids ?? []);
    setOtherCategory(p?.other_category ?? "");
    setYearsInBusiness(p?.years_in_business ?? "");
    setFundingMethods(p?.funding_methods ?? []);
    setPrequalifications(p?.prequalifications ?? []);
    setAdditionalNotes(p?.additional_notes ?? "");
    // Location: prefer the multi-select arrays, falling back to the singular
    // fields a pre-multi-select Buy Box was saved with.
    const savedCountries = (p?.preferred_country_ids ?? []).map(String);
    const savedStates = (
      p?.preferred_state_ids?.length ? p.preferred_state_ids
      : p?.preferred_state_id != null ? [p.preferred_state_id]
      : []
    ).map(String);
    const savedCities = (
      p?.preferred_city_ids?.length ? p.preferred_city_ids
      : p?.preferred_city_id != null ? [p.preferred_city_id]
      : []
    ).map(String);
    const savedCityNames =
      p?.preferred_city_names?.length ? p.preferred_city_names
      : p?.preferred_city_name ? [p.preferred_city_name]
      : [];
    setPrefCountryIds(savedCountries);
    setPrefStateIds(savedStates);
    setPrefCityIds(savedCities);
    setPrefCityNames(savedCityNames);
    defaultCountryRef.current = savedCountries.length === 0 && savedStates.length === 0;
    // Legacy Buy Boxes stored a state with no country. Resolve the owning
    // country from the unfiltered states lookup so the cascade can hydrate;
    // every save from here on stores the country ids outright.
    if (savedCountries.length === 0 && savedStates.length > 0) {
      api.get<LookupItem[]>("/lookups/states").then((all) => {
        const owning = [...new Set(
          all.filter((st) => savedStates.includes(String(st.id)))
             .map((st) => st.country_id)
             .filter((id): id is number => id != null),
        )].map(String);
        if (owning.length) setPrefCountryIds(owning);
      }).catch(() => {});
    }
    setSdePreference(p?.sde_preference ?? "");
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get<Category[]>("/lookups/categories").then(setCategories).catch(() => {});
    api.get<LookupItem[]>("/lookups/countries").then(setCountries).catch(() => {});
  }, []);

  // Only the US is seeded today, so a buyer with no saved location starts there
  // rather than facing an empty (and therefore useless) state select.
  useEffect(() => {
    if (!isOpen || !defaultCountryRef.current || countries.length === 0) return;
    defaultCountryRef.current = false;
    const us = countries.find((c) => c.name === "United States");
    if (us) setPrefCountryIds([String(us.id)]);
  }, [countries, isOpen]);

  // Options cascade: states are the union across the chosen countries, cities
  // the union across the chosen states — one request each, never N+1. These
  // effects only LOAD lists; pruning a stale selection happens in the change
  // handlers, so hydrating a saved Buy Box can't race with a cleared child.
  useEffect(() => {
    if (prefCountryIds.length === 0) { setStates([]); return; }
    let stale = false;
    const qs = prefCountryIds.map((id) => `country_ids=${encodeURIComponent(id)}`).join("&");
    api.get<LookupItem[]>(`/lookups/states?${qs}`)
      .then((rows) => { if (!stale) setStates(rows); })
      .catch(() => {});
    return () => { stale = true; };
  }, [prefCountryIds]);

  useEffect(() => {
    if (prefStateIds.length === 0) { setCities([]); return; }
    let stale = false;
    const qs = prefStateIds.map((id) => `state_ids=${encodeURIComponent(id)}`).join("&");
    api.get<LookupItem[]>(`/lookups/cities?${qs}`)
      .then((rows) => { if (!stale) setCities(rows); })
      .catch(() => {});
    return () => { stale = true; };
  }, [prefStateIds]);

  // Steps are long enough now to scroll; land every one at its heading —
  // unless we arrived from a Review pencil, which names the question to open at.
  useEffect(() => {
    const pending = pendingSectionRef.current;
    pendingSectionRef.current = null;
    const target = pending && bodyRef.current?.querySelector(`[data-section="${pending}"]`);
    if (target) target.scrollIntoView({ block: "start" });
    else bodyRef.current?.scrollTo({ top: 0 });
    measureOverflow();
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-measure when the step's content changes height rather than only on
  // scroll: the category and location lookups land after the first paint.
  useEffect(() => {
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => measureOverflow());
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen || !user) return null;

  const otherSelected = categoryIds.includes(OTHER_CATEGORY_ID);
  // The sliders can't produce an inverted range, but the manual inputs can —
  // never let min > max reach the backend (it rejects it too).
  const budgetRangeInvalid = budgetMax !== null && budgetMax < budgetMin;
  // A minimum SDE above the whole budget matches nothing (backend rejects it).
  const sdeConflict = sdeExceedsBudget(sdePreference, budgetMax);

  /* What each question is still waiting on, phrased for the footer hint. With
     several questions per step, a greyed-out Next has to name the one that is
     unanswered — and the hint links straight to it. Pre-qualification and notes
     are optional on purpose: a buyer who holds neither pre-qualification has to
     be able to move on, and an empty answer is the truthful one for them. */
  const sectionIssue: Record<string, string | null> = {
    experience: experience ? null : "your buying experience",
    funding: fundingMethods.length > 0 ? null : "at least one funding method",
    prequal: null,
    budget: budgetRangeInvalid ? "a max budget at or above your min"
      : budgetTouched ? null : "a budget range",
    sde: !sdePreference ? "an SDE target"
      : sdeConflict ? "a minimum SDE at or below your max budget"
      : null,
    years: yearsInBusiness ? null : "years in business",
    industries: categoryIds.length === 0 ? "at least one industry"
      : otherSelected && otherCategory.trim().length < 2 ? "a description for “Other”"
      : null,
    // Country, state and city are all required: sellers match on the city,
    // so a countrywide or statewide buy box was too vague to act on.
    locations: prefCountryIds.length === 0 ? "at least one country"
      : prefStateIds.length === 0 ? "at least one state"
      : prefCityIds.length + prefCityNames.length === 0 ? "at least one city"
      : null,
    review: null,
    notes: null,
  };
  /* Ticked in the on-step index. An optional question only counts as answered
     once it actually holds something. */
  const sectionAnswered: Record<string, boolean> = {
    prequal: prequalifications.length > 0,
    notes: additionalNotes.trim().length > 0,
    review: true,
  };
  const isAnswered = (id: string) => sectionAnswered[id] ?? !sectionIssue[id];

  const missingFor = (n: number) =>
    STEP_SECTIONS[n].map((id) => sectionIssue[id]).filter((m): m is string => !!m);
  const firstIssueId = (n: number) =>
    STEP_SECTIONS[n].find((id) => sectionIssue[id]) ?? null;
  const isStepValid = (n: number) => missingFor(n).length === 0;

  const stepMissing = missingFor(step);
  const nextEnabled = isStepValid(step);
  const allValid = Object.keys(STEP_SECTIONS).every((n) => isStepValid(Number(n)));

  /* Any step already cleared is reachable directly — that's what the stepper
     and the Review step's edit pencils use. */
  const canJump = (target: number) =>
    target <= step ||
    Array.from({ length: target - 1 }, (_, i) => isStepValid(i + 1)).every(Boolean);

  const scrollToSection = (id: string) => {
    bodyRef.current
      ?.querySelector(`[data-section="${id}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollDown = () => {
    const el = bodyRef.current;
    el?.scrollBy({ top: el.clientHeight * 0.8, behavior: "smooth" });
  };

  /* Answering a single-choice question carries the buyer to the next one they
     still owe an answer for. Multi-selects are left alone — scrolling away on
     every toggle would fight the buyer picking a second option. */
  const advanceAfter = (id: string) => {
    const rest = STEP_SECTIONS[step].slice(STEP_SECTIONS[step].indexOf(id) + 1);
    const next = rest.find((s) => !isAnswered(s));
    if (next) requestAnimationFrame(() => scrollToSection(next));
  };

  /* Open a specific question, switching steps first if it lives on another one. */
  const goToSection = (id: string) => {
    const target = SECTION_STEP[id];
    if (target === step) scrollToSection(id);
    else {
      pendingSectionRef.current = id;
      setStep(target);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep((s) => s - 1);
    else onClose?.();
  };

  const handleNext = () => {
    if (step < TOTAL_STEPS) setStep((s) => s + 1);
  };

  const handleSubmit = async () => {
    if (!allValid) {
      toast.error("Please complete all steps before submitting");
      return;
    }
    setSubmitting(true);
    try {
      const updated = await api.patch<UserType>("/profile/me/buyer-preferences", {
        experience,
        // 0 is sent as 0, not null: "$0 – No limit" is an answer, and the server
        // reads both-null as an unanswered budget.
        budget_min: budgetMin,
        budget_max: budgetMax,
        category_ids: categoryIds.filter((id) => id !== OTHER_CATEGORY_ID),
        other_category: categoryIds.includes(OTHER_CATEGORY_ID) ? otherCategory.trim() || null : null,
        years_in_business: yearsInBusiness || null,
        funding_methods: fundingMethods,
        prequalifications,
        additional_notes: additionalNotes.trim() || null,
        preferred_country_ids: prefCountryIds.map(Number),
        preferred_state_ids: prefStateIds.map(Number),
        preferred_city_ids: prefCityIds.map(Number),
        preferred_city_names: prefCityNames,
        preferred_location: locationLabel,
        sde_preference: sdePreference || null,
      });
      updateUser(updated);
      toast.success("Preferences saved!");
      onClose?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save preferences");
    } finally {
      setSubmitting(false);
    }
  };

  /* Location cascade. A state belongs to one country and a city to one state,
     so narrowing the parent drops the children it no longer contains. This runs
     in the change handlers rather than an effect on the id arrays: edit mode
     restores all three levels at once, and an effect would clear the lower two
     before their lookup lists arrived. */
  const applyStates = (next: string[]) => {
    setPrefStateIds(next);
    setPrefCityIds((prev) =>
      prev.filter((id) => {
        const city = cities.find((c) => String(c.id) === id);
        return city?.state_id != null && next.includes(String(city.state_id));
      }),
    );
    if (next.length === 0) setPrefCityNames([]); // a typed city still needs a state
  };

  const applyCountries = (next: string[]) => {
    setPrefCountryIds(next);
    applyStates(
      prefStateIds.filter((id) => {
        const state = states.find((st) => String(st.id) === id);
        return state?.country_id != null && next.includes(String(state.country_id));
      }),
    );
  };

  const namesFor = (list: LookupItem[], ids: string[]) =>
    ids.map((id) => list.find((o) => String(o.id) === id)?.name).filter((n): n is string => !!n);

  const selectedCityLabels = namesFor(cities, prefCityIds);
  const selectedStateNames = namesFor(states, prefStateIds);
  const selectedCountryNames = namesFor(countries, prefCountryIds);
  /* Display string, most specific first; countries only stand in when nothing
     narrower is set. The backend recomposes this canonically from the lookup
     tables on save, so what we send is only a fallback. */
  const locationParts = [...selectedCityLabels, ...prefCityNames, ...selectedStateNames];
  const locationLabel =
    (locationParts.length ? locationParts : selectedCountryNames).join(", ") || null;

  /* One removable chip per selected location, narrowest first. */
  const locationChips: { key: string; label: string; remove: () => void }[] = [
    ...prefCityIds.map((id) => ({
      key: `city-${id}`,
      label: cities.find((c) => String(c.id) === id)?.name ?? "City",
      remove: () => setPrefCityIds((prev) => prev.filter((x) => x !== id)),
    })),
    ...prefCityNames.map((name) => ({
      key: `city-typed-${name}`,
      label: name,
      remove: () => setPrefCityNames((prev) => prev.filter((x) => x !== name)),
    })),
    ...prefStateIds.map((id) => ({
      key: `state-${id}`,
      label: states.find((st) => String(st.id) === id)?.name ?? "State",
      remove: () => applyStates(prefStateIds.filter((x) => x !== id)),
    })),
    ...prefCountryIds.map((id) => ({
      key: `country-${id}`,
      label: countries.find((c) => String(c.id) === id)?.name ?? "Country",
      remove: () => applyCountries(prefCountryIds.filter((x) => x !== id)),
    })),
  ];

  const toggleCategory = (id: number) =>
    setCategoryIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const toggleFunding = (val: string) =>
    setFundingMethods((prev) => prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val]);

  const togglePrequal = (val: string) =>
    setPrequalifications((prev) => prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val]);

  const selectPreset = (min: number, max: number | null) => {
    setBudgetMin(min);
    setBudgetMax(max);
    setBudgetTouched(true);
  };

  /* Derived slider values — clamped so a manually-typed value above the slider
     ceiling can't push the track fill outside the rail. */
  const effectiveMax = budgetMax ?? MAX_BUDGET;
  const minPct = Math.min(100, (budgetMin / MAX_BUDGET) * 100);
  const maxPct = Math.min(100, (effectiveMax / MAX_BUDGET) * 100);

  const selectedCategoryNames = [
    ...categories.filter((c) => categoryIds.includes(c.id)).map((c) => c.name),
    ...(otherSelected && otherCategory.trim() ? [otherCategory.trim()] : []),
  ];

  /* Read-back on the final step; each row opens the question that owns it. */
  /* Locations read back one level per line (City / State / Country) — a single
     comma list mixed "Bakersfield, Anaheim, California" into one blur. */
  const locationLevels = [
    { label: "City", names: [...selectedCityLabels, ...prefCityNames] },
    { label: "State", names: selectedStateNames },
    { label: "Country", names: selectedCountryNames },
  ].filter((lvl) => lvl.names.length > 0);
  const locationValue: ReactNode = locationLevels.length === 0 ? "—" : (
    <div className="flex flex-col gap-1.5">
      {locationLevels.map((lvl) => (
        <div key={lvl.label} className="flex items-baseline gap-2">
          <span className="w-16 shrink-0 text-[0.65rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
            {lvl.names.length > 1 ? (lvl.label === "City" ? "Cities" : lvl.label === "Country" ? "Countries" : "States") : lvl.label}
          </span>
          <span className="min-w-0">
            {lvl.names.length > 12 ? (
              <ShowMoreList
                items={lvl.names}
                className=""
                render={(name, i) => <span key={`${name}-${i}`}>{i ? ", " : ""}{name}</span>}
              />
            ) : (
              lvl.names.join(", ")
            )}
          </span>
        </div>
      ))}
    </div>
  );

  const summaryRows: { section: string; label: string; value: ReactNode; muted?: boolean }[] = [
    { section: "experience", label: "Experience", value: labelOf(EXPERIENCE_OPTIONS, experience) || "—" },
    { section: "funding", label: "Funding", value: labelsOf(FUNDING_OPTIONS, fundingMethods).join(", ") || "—" },
    {
      section: "prequal",
      label: "Pre-qualified",
      value: labelsOf(PREQUAL_OPTIONS, prequalifications).join(", ") || "Not yet",
      muted: prequalifications.length === 0,
    },
    {
      section: "budget",
      label: "Budget",
      value: `${fmtBudget(budgetMin)} – ${budgetMax === null ? "No limit" : fmtBudget(budgetMax)}`,
    },
    { section: "sde", label: "SDE target", value: labelOf(SDE_OPTIONS, sdePreference) || "—" },
    { section: "years", label: "Years in business", value: labelOf(YEARS_OPTIONS, yearsInBusiness) || "—" },
    { section: "industries", label: "Industries", value: selectedCategoryNames.join(", ") || "—" },
    { section: "locations", label: "Locations", value: locationValue },
  ];

  const { title, sub, icon: CurrentIcon } = STEPS[step - 1];
  const isEditMode = forceOpen;

  /* ── Question blocks. Defined once and composed into the five steps below, so
        regrouping the wizard is an edit to STEPS and this composition, never a
        move of the controls themselves. ── */

  const experienceBlock = (
    <div className="flex flex-col gap-2">
      {EXPERIENCE_OPTIONS.map((opt) => {
        const Icon = opt.icon;
        return (
          <OptionCard
            key={opt.value}
            selected={experience === opt.value}
            onClick={() => { setExperience(opt.value); advanceAfter("experience"); }}
          >
            <div className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors",
              experience === opt.value
                ? "bg-primary/10 text-primary"
                : "bg-muted/70 text-muted-foreground group-hover:bg-primary/5 group-hover:text-primary/70",
            )}>
              <Icon className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className={cn(
                "text-sm font-semibold leading-tight transition-colors",
                experience === opt.value ? "text-primary" : "text-foreground",
              )}>
                {opt.label}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">{opt.sub}</p>
            </div>
          </OptionCard>
        );
      })}
    </div>
  );

  const fundingBlock = (
    <div className="flex flex-col gap-2">
      {fundingMethods.length > 0 && (
        <p className="text-xs font-medium text-primary text-right">
          {fundingMethods.length} selected
        </p>
      )}
      {FUNDING_OPTIONS.map((opt) => {
        const Icon = opt.icon;
        const sel = fundingMethods.includes(opt.value);
        return (
          <CheckCard
            key={opt.value}
            selected={sel}
            onClick={() => toggleFunding(opt.value)}
          >
            <div className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors",
              sel
                ? "bg-primary/10 text-primary"
                : "bg-muted/70 text-muted-foreground group-hover:bg-primary/5 group-hover:text-primary/70",
            )}>
              <Icon className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className={cn("text-sm font-semibold leading-tight transition-colors", sel ? "text-primary" : "text-foreground")}>
                {opt.label}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">{opt.sub}</p>
            </div>
          </CheckCard>
        );
      })}
    </div>
  );

  const prequalBlock = (
    <div className="space-y-2.5">
      {PREQUAL_OPTIONS.map((opt) => {
        const Icon = opt.icon;
        const sel = prequalifications.includes(opt.value);
        return (
          <CheckCard key={opt.value} selected={sel} onClick={() => togglePrequal(opt.value)}>
            <Icon className={cn("h-5 w-5 shrink-0", sel ? "text-primary" : "text-muted-foreground")} />
            <span className="min-w-0">
              <span className={cn("block text-sm font-bold", sel ? "text-primary" : "text-foreground")}>
                {opt.label}
              </span>
              <span className="block text-xs text-muted-foreground">{opt.sub}</span>
            </span>
          </CheckCard>
        );
      })}
    </div>
  );

  const budgetBlock = (
    <div className="space-y-5">
      {/* Preset pills */}
      <div className="grid grid-cols-3 gap-2">
        {BUDGET_PRESETS.map((preset, i) => {
          const active = budgetMin === preset.min && budgetMax === preset.max;
          return (
            <button
              key={i}
              type="button"
              onClick={() => selectPreset(preset.min, preset.max)}
              className={cn(
                "rounded-xl border px-2 py-2.5 text-center text-sm font-semibold transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
                active
                  ? "border-primary bg-gold-gradient text-primary-foreground shadow-sm"
                  : "border-border/70 bg-white/80 text-foreground hover:border-primary/40 hover:bg-primary/[0.04]",
              )}
            >
              {preset.label}
            </button>
          );
        })}
      </div>

      {/* Dual range slider */}
      <div className="space-y-2.5 px-0.5">
        {/* Current range labels */}
        <div className="flex items-center justify-between text-sm font-semibold">
          <span className="text-primary">{fmtBudget(budgetMin)}</span>
          <span className="text-xs text-muted-foreground">Budget range</span>
          <span className="text-primary">{budgetMax === null ? "No limit" : fmtBudget(budgetMax)}</span>
        </div>

        {/* Slider track */}
        <div className="relative h-8">
          <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-border/60" />
          <div
            className="absolute top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-primary pointer-events-none"
            style={{ left: `${minPct}%`, right: `${100 - maxPct}%` }}
          />
          {/* Min thumb */}
          <input
            type="range"
            min={0}
            max={MAX_BUDGET}
            step={SLIDER_STEP}
            value={budgetMin}
            onChange={(e) => {
              const v = Number(e.target.value);
              setBudgetMin(Math.min(v, effectiveMax - SLIDER_STEP));
              setBudgetTouched(true);
            }}
            className="budget-slider-thumb absolute inset-0 h-full w-full appearance-none bg-transparent"
            style={{ zIndex: budgetMin >= effectiveMax - SLIDER_STEP ? 5 : 3 }}
          />
          {/* Max thumb */}
          <input
            type="range"
            min={0}
            max={MAX_BUDGET}
            step={SLIDER_STEP}
            value={effectiveMax}
            onChange={(e) => {
              const v = Number(e.target.value);
              setBudgetMax(v >= MAX_BUDGET ? null : Math.max(v, budgetMin + SLIDER_STEP));
              setBudgetTouched(true);
            }}
            className="budget-slider-thumb absolute inset-0 h-full w-full appearance-none bg-transparent"
            style={{ zIndex: 4 }}
          />
        </div>

        {/* Tick labels */}
        <div className="flex justify-between text-xs text-muted-foreground/60 px-0.5">
          <span>$0</span>
          <span>$2.5M</span>
          <span>$5M</span>
          <span>$7.5M</span>
          <span>$10M+</span>
        </div>
      </div>

      {/* Manual min/max inputs */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <p className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">Min Budget</p>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground select-none">$</span>
            <input
              type="number"
              min={0}
              max={MAX_BUDGET}
              step={1000}
              placeholder="0"
              value={budgetMin || ""}
              onChange={(e) => {
                const v = Math.min(MAX_BUDGET, Math.max(0, Number(e.target.value) || 0));
                setBudgetMin(v);
                setBudgetTouched(true);
              }}
              className="w-full rounded-xl border border-border/70 bg-white/80 pl-7 pr-3 py-2.5 text-sm font-medium text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <p className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">Max Budget</p>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground select-none">$</span>
            <input
              type="number"
              min={0}
              step={1000}
              placeholder="No limit"
              value={budgetMax ?? ""}
              onChange={(e) => {
                setBudgetMax(e.target.value === "" ? null : Math.max(0, Number(e.target.value)));
                setBudgetTouched(true);
              }}
              className="w-full rounded-xl border border-border/70 bg-white/80 pl-7 pr-3 py-2.5 text-sm font-medium text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>
      </div>

      {budgetRangeInvalid && (
        <p className="text-xs font-medium text-destructive">
          Max budget can't be less than min budget.
        </p>
      )}
    </div>
  );

  const sdeBlock = (
    <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
      {SDE_OPTIONS.map((opt) => {
        const sel = sdePreference === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => { setSdePreference(opt.value); advanceAfter("sde"); }}
            className={cn(
              "relative flex flex-col items-center justify-center gap-1 rounded-[1.1rem] border px-3 py-5 text-center transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
              sel
                ? "border-primary/70 bg-primary/5 shadow-sm"
                : "border-border/70 bg-white/80 hover:border-primary/30 hover:bg-primary/[0.03]",
            )}
          >
            {sel && <CheckCircle2 className="absolute top-2.5 right-2.5 h-4 w-4 text-primary" />}
            <span className={cn("text-base font-bold leading-tight", sel ? "text-primary" : "text-foreground")}>
              {opt.label}
            </span>
          </button>
        );
      })}
      {sdeConflict && (
        <p className="col-span-full text-xs font-medium text-destructive">
          Minimum SDE can't be higher than your max budget ({fmtBudget(budgetMax ?? 0)}).
        </p>
      )}
    </div>
  );

  const yearsBlock = (
    <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
      {YEARS_OPTIONS.map((opt) => {
        const sel = yearsInBusiness === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => setYearsInBusiness(opt.value)}
            className={cn(
              "relative flex flex-col items-center justify-center gap-1 rounded-[1.1rem] border px-3 py-4 text-center transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
              sel
                ? "border-primary/70 bg-primary/5 shadow-sm"
                : "border-border/70 bg-white/80 hover:border-primary/30 hover:bg-primary/[0.03]",
            )}
          >
            {sel && <CheckCircle2 className="absolute top-2.5 right-2.5 h-4 w-4 text-primary" />}
            <span className={cn("text-sm font-bold leading-tight", sel ? "text-primary" : "text-foreground")}>
              {opt.label}
            </span>
            <span className="text-xs text-muted-foreground">{opt.sub}</span>
          </button>
        );
      })}
    </div>
  );

  const categoriesBlock = (
    <div className="flex flex-col gap-2">
      {categoryIds.length > 0 && (
        <p className="text-xs font-medium text-primary text-right">
          {categoryIds.filter((id) => id !== OTHER_CATEGORY_ID).length + (otherSelected ? 1 : 0)} selected
        </p>
      )}
      {categories.length === 0 && (
        <p className="py-6 text-center text-sm text-muted-foreground">Loading categories…</p>
      )}
      {categories.map((cat) => (
        <CheckCard
          key={cat.id}
          selected={categoryIds.includes(cat.id)}
          onClick={() => toggleCategory(cat.id)}
        >
          <span className={cn(
            "text-sm font-medium transition-colors",
            categoryIds.includes(cat.id) ? "text-primary" : "text-foreground",
          )}>
            {cat.name}
          </span>
        </CheckCard>
      ))}
      {/* Other */}
      <CheckCard
        selected={otherSelected}
        onClick={() => toggleCategory(OTHER_CATEGORY_ID)}
      >
        <span className={cn(
          "text-sm font-medium transition-colors",
          otherSelected ? "text-primary" : "text-foreground",
        )}>
          Other
        </span>
      </CheckCard>
      {otherSelected && (
        <>
          <input
            type="text"
            value={otherCategory}
            onChange={(e) => setOtherCategory(e.target.value)}
            placeholder="Describe the business type…"
            autoFocus
            maxLength={80}
            className="w-full rounded-xl border border-primary/40 bg-white/80 px-4 py-2.5 text-sm font-medium text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          {otherCategory.trim().length === 1 && (
            <p className="text-xs font-medium text-destructive">
              Please describe the business type (at least 2 characters).
            </p>
          )}
        </>
      )}
    </div>
  );

  /* Preferred locations — multi-select country / state / city off the same
     lookups as the listing forms, cascading downward. A city missing from the
     lookup can still be added as free text. */
  const locationBlock = (
    <div className="space-y-3.5">
      <div className="space-y-1.5">
        <p className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">Countries <span className="text-destructive">*</span></p>
        <MultiSearchableSelect
          values={prefCountryIds}
          onChange={applyCountries}
          options={countries.map((c) => ({ value: String(c.id), label: c.name }))}
          placeholder="Select one or more countries…"
          className={LOC_SELECT_CLS}
        />
        {countries.length === 0 && (
          <p className="text-xs text-muted-foreground">Loading countries…</p>
        )}
      </div>

      <div className="space-y-1.5">
        <p className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
          States <span className="text-destructive">*</span>
        </p>
        <MultiSearchableSelect
          values={prefStateIds}
          onChange={applyStates}
          options={states.map((st) => ({ value: String(st.id), label: st.name }))}
          placeholder={prefCountryIds.length === 0 ? "Select a country first" : "Select one or more states…"}
          disabled={prefCountryIds.length === 0}
          className={LOC_SELECT_CLS}
        />
      </div>

      <div className="space-y-1.5">
        <p className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
          Cities <span className="text-destructive">*</span>
        </p>
        <MultiSearchableSelect
          values={prefCityIds}
          onChange={setPrefCityIds}
          options={cities.map((c) => ({ value: String(c.id), label: c.name }))}
          placeholder={prefStateIds.length === 0 ? "Select a state first" : "Select one or more cities…"}
          disabled={prefStateIds.length === 0}
          allowCustom
          customValues={prefCityNames}
          onCustomChange={setPrefCityNames}
          className={LOC_SELECT_CLS}
        />
        {prefCityNames.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Typed{prefCityNames.length > 1 ? " cities" : " city"}:{" "}
            <span className="font-semibold text-foreground">{prefCityNames.join(", ")}</span> — saved as typed.
          </p>
        )}
      </div>

      {locationChips.length > 0 && (
        <div className="space-y-1.5 border-t border-border/60 pt-3">
          <p className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
            Targeting ({locationChips.length})
          </p>
          <ShowMoreList
            items={locationChips}
            limit={18}
            render={(chip) => (
              <span
                key={chip.key}
                className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 py-1 pl-2.5 pr-1.5 text-xs font-semibold text-foreground"
              >
                {chip.label}
                <button
                  type="button"
                  onClick={chip.remove}
                  aria-label={`Remove ${chip.label}`}
                  className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-primary/10 hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
          />
        </div>
      )}
    </div>
  );

  const summaryBlock = (
    <div className="overflow-hidden rounded-[1.25rem] border border-border/70 bg-white/70">
      {summaryRows.map((row, i) => (
        <div
          key={row.label}
          className={cn("flex items-start gap-3 px-5 py-3.5", i > 0 && "border-t border-border/50")}
        >
          <p className="w-[8.5rem] shrink-0 pt-0.5 text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">
            {row.label}
          </p>
          <div className={cn(
            "min-w-0 flex-1 text-base font-semibold leading-snug",
            row.muted ? "text-muted-foreground" : "text-foreground",
          )}>
            {row.value}
          </div>
          <button
            type="button"
            onClick={() => goToSection(row.section)}
            aria-label={`Edit ${row.label}`}
            className="shrink-0 rounded-lg border border-transparent p-1.5 text-muted-foreground transition-colors hover:border-border/70 hover:bg-primary/5 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
          >
            <Pencil className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );

  const notesBlock = (
    <div className="space-y-1.5">
      <textarea
        value={additionalNotes}
        onChange={(e) => setAdditionalNotes(e.target.value)}
        placeholder="Tell us a little about yourself, your goals, or what you're looking for..."
        rows={4}
        maxLength={1000}
        className="w-full rounded-[1.1rem] border border-border/70 bg-white/80 px-4 py-3.5 text-sm text-foreground placeholder:text-muted-foreground/50 resize-none transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      />
      <p className="text-right text-xs text-muted-foreground/60">
        {additionalNotes.length}/1000
      </p>
    </div>
  );

  /* The current step's questions, assembled from STEP_SECTIONS — the step
     bodies below never name a question directly. */
  const sectionBodies: Record<string, React.ReactNode> = {
    experience: experienceBlock,
    funding: fundingBlock,
    prequal: prequalBlock,
    budget: budgetBlock,
    sde: sdeBlock,
    years: yearsBlock,
    industries: categoriesBlock,
    locations: locationBlock,
    review: summaryBlock,
    notes: notesBlock,
  };
  const sections = STEP_SECTIONS[step].map((id) => ({
    id,
    ...SECTION_META[id],
    answered: isAnswered(id),
    body: sectionBodies[id],
  }));

  /* Portalled to <body>: AiDealScoring mounts this wizard deep inside the page
     column, where the app shell's clipping and the sticky nav/sidebar confine a
     `fixed` overlay to the content area — the nav and sidebar stayed sharp while
     everything else dimmed. From <body> the overlay always covers the whole
     viewport, and z-[100] clears the nav (z-50) and the sidebar toggle (z-60). */
  return createPortal(
    /* Full-page popup: a dialog that fills the screen edge-to-edge apart from a
       margin, floating over the dimmed app. Height comes from the padded
       `inset-0` parent — never a vh unit, which the app-wide `zoom` scales (the
       old 560px card used h-[120dvh] and spilled off the top and bottom). */
    <div className="app-zoom-reset fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6">

      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-md"
        onClick={isEditMode ? onClose : undefined}
      />

      {/* Dialog */}
      <div className="public-auth-card relative flex h-full max-h-[820px] w-full max-w-2xl flex-col overflow-hidden rounded-[1.5rem] shadow-2xl">

      {/* Top primary rule */}
      <div className="h-[3px] w-full bg-gradient-to-r from-primary via-primary-light to-primary-glow shrink-0" />

      {/* Header bar — spans the dialog, content stays in the centred column */}
      <div className="shrink-0 border-b border-border/50 bg-card/40">
        <div className="mx-auto w-full max-w-2xl px-5 sm:px-8">
          <div className="flex items-center justify-between gap-4 pt-4 pb-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/10">
                <CurrentIcon className="h-4 w-4" strokeWidth={1.7} />
              </div>
              <div className="min-w-0">
                <h2 className="heading-bold text-xl sm:text-2xl leading-tight text-foreground font-display">{title}</h2>
                <p className="mt-0.5 text-xs sm:text-[0.8rem] text-muted-foreground leading-snug">{sub}</p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2.5">
              <span className="eyebrow !text-xs text-primary/80">
                Step {step} <span className="text-muted-foreground font-medium">of {TOTAL_STEPS}</span>
              </span>
              {isEditMode && (
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-border/70 bg-muted/60 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Labelled stepper */}
          <div className="pb-2.5">
            <Stepper step={step} canJump={canJump} onJump={setStep} />
          </div>
        </div>
      </div>

      {/* Scrollable body. Top-aligned rather than centred: a step with three
          questions is taller than the dialog, and centring it clipped the
          heading off the top with no way to scroll back up to it. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div ref={bodyRef} onScroll={measureOverflow} className="flex-1 overflow-y-auto">
          <div ref={contentRef} className="mx-auto w-full max-w-2xl px-5 sm:px-8 py-4 space-y-4">

            {/* What this step asks, before the buyer has to scroll to find out.
                Counted over questions only — the Review read-back isn't one. */}
            {sections.filter((s) => s.id !== "review").length > 1 && (
              <QuestionIndex sections={sections} onJump={scrollToSection} />
            )}

            {/* The questions themselves. A step asking only one thing skips the
                per-question heading — the step heading above already said it. */}
            <div className="space-y-5">
              {sections.map((s, i) =>
                sections.length === 1 ? (
                  <div key={s.id} data-section={s.id}>{s.body}</div>
                ) : (
                  <Section
                    key={s.id}
                    id={s.id}
                    icon={s.icon}
                    title={s.title}
                    hint={s.hint}
                    optional={s.optional}
                    index={i + 1}
                    count={sections.length}
                  >
                    {s.body}
                  </Section>
                ),
              )}
            </div>

            <div className="h-2" />
          </div>
        </div>

        {/* Below-the-fold cue: the content fades out at the bottom edge and a
            pill offers the next screenful. Masking the content (rather than
            laying a gradient over it) keeps this correct in both themes. */}
        {moreBelow && (
          <>
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 [mask-image:linear-gradient(to_top,black,transparent)] backdrop-blur-[1px]" />
            <button
              type="button"
              onClick={scrollDown}
              className="absolute bottom-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-border/70 bg-card px-3 py-1.5 text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground shadow-lg transition-colors hover:border-primary/50 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            >
              More below
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </>
        )}
      </div>

      {/* Footer / navigation — pinned to the bottom of the dialog */}
      <div className="shrink-0 border-t border-border/50 bg-card/40 backdrop-blur-sm">
        <div className="mx-auto w-full max-w-2xl px-5 sm:px-8 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleBack}
              className="min-w-[88px] rounded-xl"
            >
              {step === 1 && forceOpen ? "Cancel" : "Back"}
            </Button>

            {step < TOTAL_STEPS ? (
              <Button
                onClick={handleNext}
                disabled={!nextEnabled}
                className="flex-1 rounded-xl gap-1.5"
              >
                Next
                <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={submitting || !allValid}
                className="flex-1 rounded-xl gap-1.5"
              >
                {submitting
                  ? <span className="h-4 w-4 rounded-full border-2 border-primary-foreground/40 border-t-primary-foreground animate-spin" />
                  : <Sparkles className="h-4 w-4" />
                }
                {isEditMode ? "Save preferences" : "Submit Interest"}
              </Button>
            )}
          </div>

          {/* Why Next is greyed out — with several questions per step, the buyer
              can't be left guessing which one is unanswered, so the hint names
              it and scrolls there. */}
          {stepMissing.length > 0 ? (
            <button
              type="button"
              onClick={() => { const id = firstIssueId(step); if (id) scrollToSection(id); }}
              className="mt-2 block w-full text-center text-xs font-medium leading-tight text-muted-foreground underline-offset-2 transition-colors hover:text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            >
              Still needed: {stepMissing.join(", ")}
            </button>
          ) : (
            <div className="flex items-center justify-center gap-1.5 mt-2">
              <Lock className="h-3 w-3 text-muted-foreground/50 shrink-0" />
              <p className="text-xs text-muted-foreground/70 leading-tight">
                Your information is private and only shared with matched sellers.
              </p>
            </div>
          )}
        </div>
      </div>

      </div>
    </div>,
    document.body,
  );
}
